import { useEffect, useState } from "react";

import { deleteToken, getMessaging, getToken, onMessage } from "firebase/messaging";
import { throttle } from "lodash";

const WORKER_REGISTERED_EVENT = "firebase-worker-registered";

export const dispatchWorkerRegistered = () => {
  window.dispatchEvent(new Event(WORKER_REGISTERED_EVENT));
};

export const subscribeWorkerRegistered = (callback) => {
  window.addEventListener(WORKER_REGISTERED_EVENT, callback);
  return () => window.removeEventListener(WORKER_REGISTERED_EVENT, callback);
};

export default class NotificationHelper {
  /** @returns {import("firebase/messaging").Messaging} */

  static _messagingInstance = null;

  static _serviceWorkerName = "service-worker.js";

  static get serviceWorkerName() {
    return NotificationHelper._serviceWorkerName;
  }

  static getRegistration() {
    return navigator.serviceWorker.getRegistration(`/${NotificationHelper._serviceWorkerName}`);
  }

  static async registerWorker() {
    // Delete all other service workers that are not the current one for backward compatibility
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        if (registration.active?.scriptURL.includes(NotificationHelper._serviceWorkerName)) {
          continue;
        }
        await registration.unregister();
        console.log("Unregistered service worker:", registration);
      }
    } catch (err) {
      console.error("Error unregistering service workers:", err);
    }

    try {
      const registration = await navigator.serviceWorker.register(`/${NotificationHelper._serviceWorkerName}`, {
        scope: "/",
      });

      if (registration.installing) {
        return new Promise((resolve) => {
          registration.installing.onstatechange = () => {
            if (registration.installing && registration.installing.state === "activated") {
              dispatchWorkerRegistered();
              resolve(registration);
              return;
            } else if (registration.installing && registration.installing.state === "redundant") {
              console.log("Service worker is redundant");
            }
            if (registration.active) {
              dispatchWorkerRegistered();
              resolve(registration);
            }
          };
        });
      } else if (registration.active) {
        dispatchWorkerRegistered();
        return registration;
      } else {
        throw new Error("Service worker is not active");
      }
    } catch (err) {
      console.error("Service worker registration failed:", err);
      throw err;
    }
  }

  static async unregisterWorker() {
    const registration = await NotificationHelper.getRegistration();
    if (registration) {
      await registration.unregister();
    }
    dispatchWorkerRegistered();
  }
  static getMessagingInstance() {
    if (NotificationHelper._messagingInstance) {
      return NotificationHelper._messagingInstance;
    }
    const messaging = getMessaging();
    NotificationHelper._messagingInstance = messaging;
    return messaging;
  }

  /**
   * @param {string} vapidKey
   * @returns {Promise<string | null>}
   */
  static async getFirebaseToken(vapidKey) {
    const messaging = NotificationHelper.getMessagingInstance();
    const registration = await NotificationHelper.getRegistration();
    if (!registration) {
      console.error("Service worker registration not found.");
      return null;
    }

    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
    return token;
  }
  static async deleteFirebaseToken() {
    const messaging = NotificationHelper.getMessagingInstance();
    const registration = await NotificationHelper.getRegistration();
    if (!registration) {
      console.warn("Service worker registration not found for deleting token.");
      return;
    }
    try {
      await deleteToken(messaging, {
        serviceWorkerRegistration: registration,
      });
    } catch (error) {
      console.error("Error deleting Firebase token:", error);
    }
  }

  static onForegroundMessage(callback) {
    const messaging = NotificationHelper.getMessagingInstance();
    onMessage(messaging, callback);
  }
  // 5dk'lık kalp atış interval'i (ms)
  static _heartbeatIntervalMs = 10 * 1000;
  // 2dk'lık AFK timeout süresi (ms)
  static _afkTimeoutMs = 3 * 1000;
  static _heartbeatInterval = null;
  static _afkTimer = null;
  static _isAfk = false;
  static _activityEvents = ["mousemove", "keydown", "scroll", "touchstart"];
  static _throttleDelay = 500;
  static _isHeartbeatIntervalSkipped = false;

  static tabActivityListener(callbackBase) {
    const callback = async (isActive, instant) => {
      if (isActive && !instant) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }
      callbackBase(isActive);
    };

    function resetAfkTimer() {
      if (NotificationHelper._isHeartbeatIntervalSkipped) {
        NotificationHelper._isHeartbeatIntervalSkipped = false;
        callback(true, true);
      }
      clearTimeout(NotificationHelper._afkTimer);

      NotificationHelper._isAfk = false;
      NotificationHelper._afkTimer = setTimeout(() => {
        NotificationHelper._isAfk = true;
      }, NotificationHelper._afkTimeoutMs);
    }

    const onUserActivity = throttle(() => {
      if (document.visibilityState === "visible") {
        resetAfkTimer();
      }
    }, NotificationHelper._throttleDelay);

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        callback(true);

        clearInterval(NotificationHelper._heartbeatInterval);
        NotificationHelper._heartbeatInterval = setInterval(() => {
          if (!NotificationHelper._isAfk) {
            callback(true);
          } else {
            NotificationHelper._isHeartbeatIntervalSkipped = true;
          }
        }, NotificationHelper._heartbeatIntervalMs);

        resetAfkTimer();
        NotificationHelper._activityEvents.forEach((evt) => document.addEventListener(evt, onUserActivity));
      } else {
        callback(false);
        clearInterval(NotificationHelper._heartbeatInterval);
        clearTimeout(NotificationHelper._afkTimer);
        NotificationHelper._activityEvents.forEach((evt) => document.removeEventListener(evt, onUserActivity));
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    handleVisibilityChange();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(NotificationHelper._heartbeatInterval);
      clearTimeout(NotificationHelper._afkTimer);
      NotificationHelper._activityEvents.forEach((evt) => document.removeEventListener(evt, onUserActivity));
    };
  }
}

const useFirebaseWorkerExists = () => {
  const [workerExists, setWorkerExists] = useState(false);
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    let mounted = true;

    NotificationHelper.getRegistration().then((registration) => {
      if (mounted) {
        const isExistAndActive = !!registration && !!registration.active;
        setRegistration(registration);
        setWorkerExists(isExistAndActive);
      }
    });

    const unsubscribe = subscribeWorkerRegistered(() => {
      NotificationHelper.getRegistration().then((registration) => {
        if (mounted) {
          const isExistAndActive = !!registration && !!registration.active;
          setRegistration(registration);
          setWorkerExists(isExistAndActive);
        }
      });
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [registration]);

  return workerExists;
};
export { useFirebaseWorkerExists };
